/**
 * One widget on the Overview grid (screenshots/67–71): the card, its header (icon · title · meta · ⋯) and its body.
 * S / M / L come from the widget's OWN cells (71), measured with a ResizeObserver so a widget being resized switches
 * layout at each breakpoint (body keyed by size → recipes.sizeCrossfade, 150ms). First paint uses the saved cells.
 * In customize mode the card is focusable and draggable; Grid.tsx owns the pointer and keyboard logic.
 */
import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState, type HTMLAttributes, type ReactNode } from "react";
import type { UserGroup } from "../nav/navConfig";
import { widgetSize } from "./charts/chartTypes";
import { GAP_PX as GAP, ROW_PX as ROW_H } from "./grid/layoutEngine";
import { OvIcon } from "./icons";
import type { LayoutItem, OverviewSnapshot, WidgetFeeds } from "./types";
import { BODIES, header } from "./widgets/bodies";
import { crossFilterFeeds, crossTargets, setOverviewFilter, shortName, useOverviewFilter } from "./chartEditorStore";
import { WidgetMenu } from "./WidgetMenu";
import { StateView } from "./widgets/DataStates";
import { AddToDashboardMenu } from "../analytics/AddToDashboard";
import { useAnalytics } from "../analytics/analyticsStore";
import { useOverview, useOverviewLayout } from "./overviewStore";

const SIZE_WORD = { S: "small", M: "medium", L: "large" } as const;

/** The widget's own cells from its rendered size (ResizeObserver), starting from the saved cells. */
function useOwnCells(ref: React.RefObject<HTMLElement | null>, colW: number, w: number, h: number) {
  const [cells, setCells] = useState({ w, h });
  useLayoutEffect(() => setCells({ w, h }), [w, h]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => {
      const box = e.borderBoxSize?.[0];
      const pw = box ? box.inlineSize : e.contentRect.width, ph = box ? box.blockSize : e.contentRect.height;
      if (!pw || !ph) return;
      const next = { w: Math.max(1, Math.round((pw + GAP) / (colW + GAP))), h: Math.max(1, Math.round((ph + GAP) / (ROW_H + GAP))) };
      setCells((c) => (c.w === next.w && c.h === next.h ? c : next));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, colW]);
  return cells;
}

export interface FrameProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  item: LayoutItem;
  colW: number;
  group: UserGroup;
  feeds: WidgetFeeds;
  snapshot: OverviewSnapshot | null;
  customizing: boolean;
  selected: boolean;
  removing: boolean;
  style: React.CSSProperties;
  /** Customize chrome: handles, drag pill, size tag (Grid.tsx). */
  chrome?: ReactNode;
  onRemove?: () => void;
}

export const WidgetFrame = memo(function WidgetFrame({ item, colW, group, feeds, snapshot, customizing, selected, removing, chrome, onRemove, className, ...rest }: FrameProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { w, h } = useOwnCells(ref, colW, item.w, item.h);
  const size = widgetSize(w, h);
  // A member cross-filter (89) narrows the member widgets' lists; data widgets re-query on their own (DataWidget).
  const filter = useOverviewFilter();
  const shown = useMemo(() => crossFilterFeeds(feeds, filter), [feeds, filter]);
  // Header slot: data widgets put their state chips here ("Still loading · 4 s", "Snapshot 6:02 AM · reconnecting", Reset zoom; 88, 89).
  const [slot, setSlot] = useState<HTMLSpanElement | null>(null);
  const [menu, setMenu] = useState(false);
  const menuBtn = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback((focusBack: boolean) => { setMenu(false); if (focusBack) menuBtn.current?.focus(); }, []);
  const props = { item, size, w, h, group, feeds: shown, snapshot, slot };
  const { title, meta, icon } = header(props);
  const Body = BODIES[item.type];
  // Feeds still waiting for a producer come from sample values (snapshot.sample): the widget says so.
  const sampleKeys: readonly string[] = snapshot?.sample ?? [];
  const sample = item.type === "number" ? !!item.config.metric && (sampleKeys.includes("metrics") || sampleKeys.includes(item.config.metric)) : sampleKeys.includes(item.type);
  // 90: widgets the other editor touched get a small "Jordan moved this" tag while customizing.
  const movedBy = useOverviewLayout((s) => (s.lastMerge?.changedByThem.includes(item.id) ? s.lastMerge.by : null));
  const liveBy = useOverview((s) => (s.presence?.touched.includes(item.id) ? s.presence.name : null));
  const mover = customizing ? liveBy ?? movedBy : null;
  const readOnly = useOverview((s) => s.readOnly);
  // Public dashboard links (§2.13): a team-only widget keeps its cells and says so ("Only your team can see this").
  const redacted = !!(item.config as { redacted?: boolean }).redacted;
  // 89 "Add to a dashboard…": offered when you have dashboards (Analytics).
  const hasDashboards = useAnalytics((s) => s.titles.length > 0);
  const [adding, setAdding] = useState(false);
  const ringed = !!filter && filter.sourceWidgetId !== item.id && crossTargets([item], filter.field, filter.sourceWidgetId).length > 0;
  const cls = ["ov-widget", `ov-widget--${size}`, ringed && "is-filtered", customizing && "is-customizing", selected && "is-selected", removing && "is-removing", className].filter(Boolean).join(" ");
  return (
    <div ref={ref} className={cls} data-type={item.type} data-flip-key={item.id} id={`w-${item.id}`} role="region" aria-label={`${title}, ${SIZE_WORD[size]}`} {...rest}>
      <div className="ov-widget__head">
        <OvIcon name={icon} />
        <h3 className="ov-widget__title">{title}</h3>
        {meta && <span className="ov-widget__meta">{meta}</span>}
        {customizing && <button className="ov-widget__remove" aria-label={`Remove ${title}`} onClick={onRemove} onPointerDown={(e) => e.stopPropagation()}><OvIcon name="x" /></button>}
        <span className="ov-widget__slot" ref={setSlot} />
        <FilterChip item={item} />
        <button ref={menuBtn} type="button" className="ov-widget__menu ov-widget__menu--btn" aria-label="Widget menu" aria-haspopup="menu" aria-expanded={menu}
          onPointerDown={(e) => e.stopPropagation()} onClick={() => setMenu((v) => !v)}><OvIcon name="ellipsis" /></button>
        {menu && <WidgetMenu item={item} title={title} anchor={menuBtn.current} onClose={closeMenu} readOnly={readOnly || redacted}
          onAddToDashboard={hasDashboards ? () => setAdding(true) : undefined} />}
        {adding && <AddToDashboardMenu item={item} anchor={menuBtn.current} onClose={() => setAdding(false)} />}
      </div>
      {mover && <span className="ov-moved-tag"><OvIcon name="users" size={11} />{mover} moved this</span>}
      <div className="ov-widget__body" key={size}>
        {redacted ? <StateView state={{ kind: "forbidden", reason: "teamOnly" }} /> : <Body {...props} />}
      </div>
      {chrome}
    </div>
  );
});

/** The cross-filter chip on widgets a chart click filters (79, 81 #9): "⛛ Nugent ×"; × clears it everywhere. */
function FilterChip({ item }: { item: LayoutItem }) {
  const f = useOverviewFilter();
  if (!f || f.sourceWidgetId === item.id || !crossTargets([item], f.field, f.sourceWidgetId).length) return null;
  return (
    <span className="ov-filter-chip"><OvIcon name="funnel" size={11} />{shortName(f.label)}
      <button type="button" aria-label={`Clear the ${f.label} filter`} onPointerDown={(e) => e.stopPropagation()} onClick={() => setOverviewFilter(null)}><OvIcon name="x" size={11} /></button>
    </span>
  );
}
